import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useEntitlements } from "@/lib/useEntitlements";
import PaywallModal from "@/components/PaywallModal";
import {
  cooldownRetry, formatScope, MAX_SCAN_PLATFORMS,
} from "@/components/onboarding/shared";
import {
  genSessionKey, genDraftKey, hasBrandDraftScope, FLOW_NEW_BRAND,
} from "@/lib/brandDraft";
import { ChatLayout, Assistant, UserReply } from "@/components/onboarding/ChatShell";
import { BrandGenLoader } from "@/components/onboarding/GenLoaders";
import {
  ChatCategoryStep, ChatScopeStep, ChatUrlComposer, FirstScanStep, ChatReview,
} from "@/components/onboarding/ChatSteps";
import InterviewStep from "@/components/onboarding/InterviewStep";

/**
 * Pick the fresher of two interview states. See applyGen for why this exists:
 * the poll and the answer POST both write it, and they can land out of order.
 */
function newerInterview(a, b) {
  if (!b) return a;
  if (!a) return b;
  if (a.state === "resolved" && b.state !== "resolved") return a;
  if (b.state === "resolved") return b;
  const n = (x) => Object.keys(x?.answers || {}).length;
  return n(b) >= n(a) ? b : a;
}

/**
 * Onboarding — one live chat conversation.
 *
 * Same steps, same backend calls as before, just rendered as a chat:
 *   1. category  → /onboarding/select-category
 *   2. scope     → /onboarding/select-market-scope
 *   3. website   → /brands/generate
 *   4. review    → /brands/confirm → Find Customers
 *
 * The assistant asks on the left (✨ avatar), the user's answers show as
 * reply bubbles on the right, and each step's picker/input appears in-line
 * as quick-replies under the latest question.
 */
export default function Onboarding() {
  const navigate = useNavigate();
  const {
    workspace,
    workspaceEpoch,
    refreshWorkspace,
    refreshBrand,
    switchBrand,
    logout,
    user,
  } = useAuth();

  /**
   * ── ADD-BRAND MODE (`/onboarding?new=1`) ──────────────────────────────────
   *
   * The same conversation, run for an ADDITIONAL brand. Two things must differ,
   * and both are load-bearing:
   *
   *  1. The "you already have a brand, go to the app" redirect below must not
   *     fire. It is correct for someone who wandered back to /onboarding; it is
   *     the whole obstacle for someone who deliberately asked to add a brand.
   *
   *  2. Category and market must be ASKED AGAIN, even though the workspace
   *     already has both. Brands in a workspace are independent clients (owner
   *     decision, 20 Jul), so brand #2 inheriting brand #1's country and
   *     category is not a shortcut, it is a wrong answer that never surfaces —
   *     a D2C label added to a Kolkata coaching workspace would be scanned as
   *     local-Kolkata and nobody would be told. The backend was changed to
   *     accept per-brand `business_category` / `market_scope` / `market_city`
   *     precisely so the client could stop inheriting; skipping the questions
   *     here would leave that change unused and the defect intact.
   *
   * The answers are collected LOCALLY (`localOnly` on both steps) and travel on
   * `POST /brands/generate`. They are deliberately NOT written to the workspace:
   * retagging the workspace would change brand #1's market as a side effect of
   * creating brand #2 — the same bug pointing the other way.
   */
  const newMode = React.useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("new") === "1";
    } catch (_) {
      return false;
    }
  }, []);
  // Brand #2's own market, held here until generate. `null` means "not answered
  // yet in this run" — distinct from "the workspace has none", which is what
  // the non-new path reads.
  const [newCategoryId, setNewCategoryId] = useState(null);
  const [newScope, setNewScope] = useState(null);
  const [newCity, setNewCity] = useState("");
  // Live window onto the epoch for the generation poll — see startPolling. A
  // ref, not the state value: the poll closes over its scope once, at start.
  const epochRef = React.useRef(workspaceEpoch);
  epochRef.current = workspaceEpoch;

  // Draft/job storage is scoped to THIS workspace. Until the workspace has
  // loaded we have no key, and we neither read nor write — a guessed key would
  // either resurrect another workspace's draft or write one nothing finds again.
  const wsId = workspace?.workspace_id;
  const wsScoped = hasBrandDraftScope(wsId);
  // Add-brand drafts live under their own key. They carry a DIFFERENT market
  // from a first-run draft, so restoring one as the other would silently swap a
  // brand's market — see lib/brandDraft.js.
  const flow = newMode ? FLOW_NEW_BRAND : undefined;
  const jobKey = wsScoped ? genSessionKey(wsId, flow) : null;
  const draftKey = wsScoped ? genDraftKey(wsId, flow) : null;

  const initialStep = (() => {
    // Add-brand always starts at category. The workspace's own fields say
    // nothing about the brand being created, so "already answered" does not
    // apply — that is the entire point of `?new=1`.
    if (newMode) return "category";
    if (!workspace?.business_category) return "category";
    if (!workspace?.market_scope) return "scope";
    return "input";
  })();
  const [step, setStep] = useState(initialStep);

  // Transcript memory — what the user has already answered (for reply bubbles)
  const [categoryLabel, setCategoryLabel] = useState(workspace?.business_category_label || "");
  const [scopeLabel, setScopeLabel] = useState("");
  const [scopeCity, setScopeCity] = useState(workspace?.market_city || "");

  const [url, setUrl] = useState("");
  const [dumpMode, setDumpMode] = useState(false); // no-website path: paste brand content
  const [dump, setDump] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [brand, setBrand] = useState({ name: "", website_url: "", description: "" });
  // First-scan step (after brand confirm): the confirmed brand + scan-launch state.
  const [confirmedBrandId, setConfirmedBrandId] = useState(null);
  const [firstScanRunning, setFirstScanRunning] = useState(false);

  // Brand-generation job (POST → poll). `gen` holds the latest server status so
  // the progress bar is driven off REAL milestones, not a local clock.
  const [gen, setGen] = useState(null);
  const [liveMsg, setLiveMsg] = useState("");            // tight aria-live text
  const [revealHeadline, setRevealHeadline] = useState(""); // Ori's visible reveal line
  // "Email me when it's ready" — lifted up here (not local to one loader) so it
  // survives the BrandGenLoader → GenRibbon handoff at ~20% instead of resetting.
  const [notified, setNotified] = useState(false);
  const genPollRef = useRef({ stop: false });
  const genBusyRef = useRef(false);   // in-flight guard: one generation job at a time
  const revealRef = useRef({ profile: false, icps: false, keywords: false });
  const genDone = gen?.status === "done";
  const { loaded: entLoaded, brandAnalysisAvailable, brandAnalysisUnblocksAt } = useEntitlements();
  // Three states. Until entitlements land we know neither that they're blocked
  // nor that they're clear, so we say nothing — an unearned "you just ran one,
  // wait 30 minutes" on a first-ever visit would be a flat lie. (cooldownRetry
  // already returns null for a null timestamp; the guard makes that deliberate
  // rather than a lucky accident.)
  const cooldown = !entLoaded || brandAnalysisAvailable
    ? null
    : cooldownRetry(brandAnalysisUnblocksAt);

  const scrollRef = useRef(null);
  useEffect(() => {
    // Keep the newest message in view as the conversation grows — but NEVER yank
    // the view while the user is typing in a revealed field (reveals arrive async
    // while they edit the brand card).
    const el = document.activeElement;
    const editing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA") &&
      scrollRef.current?.contains(el);
    if (editing) return;
    const t = setTimeout(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, 60);
    return () => clearTimeout(t);
  }, [step, draft]);

  useEffect(() => {
    // `?new=1` means "I asked for another brand". This guard exists to catch
    // someone who wandered back to /onboarding with a finished setup; bouncing
    // a deliberate add-brand request would make the feature unreachable.
    if (newMode) return;
    (async () => {
      try {
        const resp = await api.get("/brands");
        const list = resp.data?.items || [];
        const confirmed = list.find((b) => b.status === "confirmed");
        const hasCategory = !!workspace?.business_category;
        const hasScope = !!workspace?.market_scope;
        if (confirmed && hasCategory && hasScope) {
          await refreshBrand();
          sessionStorage.removeItem("lm_skip_onboarding");
          navigate("/app/find-customers", { replace: true });
        }
      } catch (_) {}
    })();
  }, [navigate, refreshBrand, workspace?.business_category, workspace?.market_scope, newMode]);

  // Fold a poll response into local state: reveal each section the MOMENT it
  // lands (profile ~20% → ICPs ~40% → keywords ~92%), without ever clobbering an
  // edit the user has already made to an earlier section.
  const applyGen = React.useCallback((s) => {
    // The interview is written by TWO sources — the 1.2s status poll and the
    // answer POST's own response — and they can land out of order: a poll fired
    // before the POST can resolve after it and would drag the cursor back to the
    // question just answered, re-asking it. So the newer state wins, where
    // "newer" is resolved-beats-pending then more-answers-beats-fewer. Both only
    // ever grow server-side, so this cannot get stuck ahead of the truth.
    setGen((prev) => ({ ...s, interview: newerInterview(prev?.interview, s.interview) }));

    // Hold the reveal while the interview is open. The profile lands at 20%,
    // BEFORE the interview finishes, and the old code switched to the review
    // step the instant it saw one — which would yank the question the owner is
    // mid-way through typing off the screen and replace it with a brand card.
    // Phase 2 is blocked on them anyway, so there is nothing to reveal yet.
    const interviewOpen = !!s.interview && s.interview.state !== "resolved";

    if (s.profile && !revealRef.current.profile && !interviewOpen) {
      revealRef.current.profile = true;
      setDraft((prev) => ({
        profile: s.profile,
        icps: prev?.icps || [],
        keywords: prev?.keywords || [],
      }));
      setBrand({
        name: s.profile.name || "",
        website_url: s.website_url || (dumpMode ? "" : url),
        description: s.profile.brand_summary || "",
      });
      // Restore the website reply bubble after a mid-generation refresh.
      if (!dumpMode && !url && s.website_url) setUrl(s.website_url);
      setStep("review");
      setRevealHeadline("Your brand's ready — take a look while I keep going.");
      setLiveMsg("Your brand's ready.");
    }
    if (s.icps && !revealRef.current.icps) {
      revealRef.current.icps = true;
      setDraft((prev) => ({ ...(prev || {}), icps: s.icps }));
      setRevealHeadline("Got your ideal customers — see who I found.");
      setLiveMsg("Your ideal customers are ready.");
    }
    if (s.keywords && !revealRef.current.keywords) {
      revealRef.current.keywords = true;
      setDraft((prev) => ({ ...(prev || {}), keywords: s.keywords }));
      setRevealHeadline("Your buyer words are in. You're all set to review.");
      setLiveMsg("Your buyer words are ready.");
    }
    if (s.status === "done") {
      // `draft` is the source of truth for confirm. Keep any user edits; fill in
      // anything they haven't touched from the final draft.
      const d = s.draft || {};
      setDraft((prev) => {
        const next = {
          profile: prev?.profile || d.profile,
          icps: prev?.icps?.length ? prev.icps : (d.icps || []),
          keywords: prev?.keywords?.length ? prev.keywords : (d.keywords || []),
          // survive a refresh before Confirm (see lib/brandDraft.js)
          _meta: { website_url: s.website_url || url, dumpMode },
        };
        if (draftKey) {
          try { sessionStorage.setItem(draftKey, JSON.stringify(next)); } catch (_) {}
        }
        return next;
      });
      setGenerating(false);
      genBusyRef.current = false;
      if (jobKey) sessionStorage.removeItem(jobKey);
      refreshWorkspace().catch(() => {});
    }
  }, [dumpMode, url, draftKey, jobKey, refreshWorkspace]);

  // Brand-generation poll. Two independent stop conditions, and both are needed:
  //
  //   · `genPollRef.current.stop` — the existing one. Set by the resume effect's
  //     cleanup and by terminal statuses.
  //   · the workspace EPOCH captured at start — Phase 5.5. A generation job runs
  //     for minutes; a switch mid-generation must not carry workspace A's brand
  //     into workspace B's onboarding. The cleanup alone is not sufficient,
  //     because `runGenerate` starts a poll OUTSIDE that effect and nothing
  //     tears it down.
  const startPolling = React.useCallback((jobId) => {
    genPollRef.current.stop = false;
    const startedInEpoch = epochRef.current;
    let fails = 0;
    const poll = async () => {
      if (genPollRef.current.stop) return;
      if (epochRef.current !== startedInEpoch) {
        // Silent: this job belongs to the workspace the user just left, and it
        // is still running there. Nothing about the workspace they are now in
        // has gone wrong, so nothing here may say that it has.
        genPollRef.current.stop = true;
        return;
      }
      try {
        const r = await api.get(`/brands/generate/${jobId}/status`);
        fails = 0;
        applyGen(r.data);
        if (r.data.status === "done" || r.data.status === "error") {
          genPollRef.current.stop = true;
          if (r.data.status === "error") { setGenerating(false); genBusyRef.current = false; }
          return;
        }
      } catch (e) {
        // Tolerate transient blips (throttled 3G, tab throttling); give up only
        // after several consecutive failures.
        if (++fails >= 6) {
          genPollRef.current.stop = true;
          setGen((g) => ({ ...(g || {}), status: "error",
            error: "Lost connection while working on your brand." }));
          setGenerating(false);
          genBusyRef.current = false;
          return;
        }
      }
      if (!genPollRef.current.stop) setTimeout(poll, 1200);
    };
    poll();
  }, [applyGen]);

  const runGenerate = async (bodyOverride) => {
    // Guard against a double-click spawning two backend jobs (each burns ~150s
    // of LLM work and stamps the 24h cooldown).
    if (genBusyRef.current) return;
    genBusyRef.current = true;
    if (draftKey) sessionStorage.removeItem(draftKey); // a fresh run supersedes any saved draft
    setGen(null);
    setDraft(null);
    setNotified(false);   // a fresh job hasn't been opted into email-notify yet
    revealRef.current = { profile: false, icps: false, keywords: false };
    setRevealHeadline("");
    setLiveMsg("");
    setGenerating(true);
    setStep("generating");
    try {
      // Either crawl a website OR read the pasted brand dump (no-website path).
      const body = bodyOverride || (dumpMode ? { content_dump: dump.trim() } : { website_url: url });
      // THE BRAND'S OWN MARKET. Sent only in add-brand mode, where the user was
      // just asked; in first-run mode these are absent and the backend falls
      // back to the workspace's, which keeps brand #1 byte-identical to before.
      //
      // `market_city` rides with `market_scope` deliberately — the backend
      // discards the city unless the caller also declared the scope, so sending
      // one without the other would silently drop it.
      if (newMode) {
        if (newCategoryId) body.business_category = newCategoryId;
        if (newScope) {
          body.market_scope = newScope;
          body.market_city = newScope === "local_city" ? newCity : null;
        }
      }
      const resp = await api.post("/brands/generate", body);
      const jobId = resp.data.job_id;
      if (jobKey) sessionStorage.setItem(jobKey, JSON.stringify({ job_id: jobId, dumpMode }));
      // Seed the bar from the immediate ack (status:"running", started_at).
      setGen({ status: "running", percent: 0, stage: "start", ...resp.data });
      startPolling(jobId);
    } catch (e) {
      // 429 rate-limit sends detail as an OBJECT ({rate_limited, message, …}) —
      // getErrorMessage extracts the human message so it never renders as
      // "[object Object]"; `detail` itself is still read below for rate_limited.
      const detail = e?.response?.data?.detail;
      toast.error(getErrorMessage(e, "Could not start brand analysis."));
      setStep("input");
      setGenerating(false);
      genBusyRef.current = false;
      // Cooldown just tightened → refresh entitlements so the inline note shows.
      if (detail?.rate_limited) refreshWorkspace().catch(() => {});
    }
  };

  const retryGenerate = () => {
    const src = gen || {};
    if (src.content_dump) { setDumpMode(true); setDump(src.content_dump); runGenerate({ content_dump: src.content_dump }); }
    else if (src.website_url) { setDumpMode(false); setUrl(src.website_url); runGenerate({ website_url: src.website_url }); }
    else runGenerate();
  };

  // Shared "email me when it's ready" handler — used by BOTH BrandGenLoader
  // (pre-profile) and GenRibbon (post-profile, while ICPs/keywords still stream)
  // so the opt-in stays available for the WHOLE wait, not just the first ~20%.
  const handleNotify = React.useCallback(async () => {
    if (!gen?.job_id) return;
    setNotified(true);
    try { await api.post(`/brands/generate/${gen.job_id}/notify`); }
    catch (_) { setNotified(false); }
  }, [gen?.job_id]);

  // One interview answer (or one skip). Deliberately one request per question,
  // not a batch at the end: a batch means a closed tab loses everything typed so
  // far, and the whole point of this feature is that it survives being left.
  const submitInterview = React.useCallback(async (questionId, { answer, skipped }) => {
    const jobId = gen?.job_id;
    if (!jobId) return;
    try {
      const r = await api.post(`/brands/generate/${jobId}/interview`, {
        question_id: questionId,
        answer: skipped ? null : answer,
        skipped: !!skipped,
      });
      setGen((g) => ({ ...(g || {}), interview: newerInterview(g?.interview, r.data.interview) }));
    } catch (e) {
      if (e?.response?.status === 409) {
        // Not an error — the interview is already closed (a duplicate submit, a
        // retry that actually landed the first time, or a second tab that
        // finished it). The server sends the authoritative state back with the
        // 409, so we adopt it and move on rather than showing a failure for
        // something that succeeded.
        const iv = e.response?.data?.detail?.interview;
        if (iv) setGen((g) => ({ ...(g || {}), interview: newerInterview(g?.interview, iv) }));
        return;
      }
      // Anything else: the answer did not land. Say so and leave their text in
      // the box — submitting the same question again just overwrites, so a
      // retry is always safe.
      // "Your answer's still here" is the clause that matters: the text is not
      // cleared on failure and retry overwrites the same question, so the one
      // thing the owner fears — having to retype it — cannot happen. Doesn't
      // point at Skip; that control is already on screen at full weight, and
      // naming it here would read as "give up".
      toast.error(getErrorMessage(e, "That didn't send. Your answer's still here — try again."));
      throw e;
    }
  }, [gen?.job_id]);

  // Refresh / tab-return resume: if a job was mid-flight, re-attach and poll the
  // SAME job (position reconstructed from server started_at/elapsed_ms/percent).
  useEffect(() => {
    const pollCtl = genPollRef.current;
    // Cancellation for THIS run of the effect, kept separate from `pollCtl.stop`.
    //
    // Path 3 below is async, so it has to re-check on the way back that it is
    // still wanted. It used to re-check `pollCtl.stop` — but that ref is SHARED
    // across runs, and the previous run's cleanup sets it to true on the way
    // out. So the very first thing every re-run saw was `stop === true`, left
    // there by its predecessor, and it skipped the probe. Consequence: the
    // tab-close resume silently did nothing after a workspace switch (and, under
    // StrictMode's double-invoke, on every dev mount). A per-run flag cannot be
    // poisoned by a run that has already finished.
    let cancelled = false;
    // Draft/job storage is per-workspace, so we cannot look for one until we
    // know which workspace this is. Re-running when wsId changes is REQUIRED,
    // not incidental: it is what stops workspace A's resume from running under
    // workspace B, and the cleanup below stops A's poll on the way out.
    if (!wsScoped) return undefined;
    // 0) Server-backed resume from a link (e.g. the "your brand is ready" email):
    //    /onboarding?job=<id>. Restores from the SERVER — so it works even after the
    //    tab was closed or on another device (not just this tab's sessionStorage).
    //    startPolling → applyGen handles BOTH a still-running job (keeps the loader)
    //    and an already-done job (reveals the finished brand + jumps to review).
    const jobFromUrl = new URLSearchParams(window.location.search).get("job");
    if (jobFromUrl) {
      setGenerating(true);
      setStep("generating");
      startPolling(jobFromUrl);
      try { window.history.replaceState({}, "", window.location.pathname); } catch (_) {}
      return () => { cancelled = true; pollCtl.stop = true; };
    }
    // 1) A finished-but-unconfirmed draft survives refresh → restore the review
    //    directly, so the user is never dropped to an empty website step (which
    //    would 429 on the 24h cooldown). Cleared only on confirm / a fresh run.
    let savedDraft = null;
    // `flow` MUST be passed here. Generation writes the flow-scoped key, so
    // reading the un-scoped one would never resume an add-brand draft — and, far
    // worse, would restore the FIRST-RUN draft into the add-brand flow, where it
    // would be confirmed carrying the workspace's market instead of the one the
    // user just chose. That is the market-inheritance defect re-entering through
    // the resume path.
    try { savedDraft = JSON.parse(sessionStorage.getItem(genDraftKey(wsId, flow)) || "null"); } catch (_) {}
    if (savedDraft?.profile) {
      const meta = savedDraft._meta || {};
      revealRef.current = { profile: true, icps: true, keywords: true };
      setDraft(savedDraft);
      if (typeof meta.dumpMode === "boolean") setDumpMode(meta.dumpMode);
      if (meta.website_url) setUrl(meta.website_url);
      setBrand({
        name: savedDraft.profile.name || "",
        website_url: meta.dumpMode ? "" : (meta.website_url || ""),
        description: savedDraft.profile.brand_summary || "",
      });
      setGen({ status: "done", percent: 100 });
      setStep("review");
      return () => { cancelled = true; pollCtl.stop = true; };
    }
    // 2) A still-running job → re-attach and poll the SAME job (position
    //    reconstructed server-side from started_at/elapsed_ms/percent).
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(genSessionKey(wsId, flow)) || "null"); } catch (_) {}
    if (saved?.job_id) {
      if (typeof saved.dumpMode === "boolean") setDumpMode(saved.dumpMode);
      setGenerating(true);
      setStep("generating");
      startPolling(saved.job_id);
      return () => { cancelled = true; pollCtl.stop = true; };
    }
    // 3) Nothing in THIS browser's storage — but the job itself lives on the
    //    server, tied to the workspace, not the tab. Ask directly: this is what
    //    makes resume survive a FULL tab close (not just a same-tab refresh),
    //    with no email link needed. Mirrors the scan loader's own resume
    //    (GET /scans?brand_id=...&limit=1) — same trick, zero client storage.
    (async () => {
      try {
        const r = await api.get("/brands/generate/active");
        const jobId = r.data?.job_id;
        if (jobId && !cancelled) {
          setGenerating(true);
          setStep("generating");
          startPolling(jobId);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; pollCtl.stop = true; };
    // `startPolling` is deliberately NOT a dependency and the suppression must
    // stay. It is a useCallback over applyGen, which closes over `url` and
    // `dumpMode` — so its identity changes on EVERY KEYSTROKE in the website
    // field. Listing it would tear down and restart the resume poll (and the
    // GET /brands/generate/active probe) on each character typed. wsId and the
    // workspace epoch are the only dependencies that should re-run this effect,
    // and both are listed.
    //
    // The epoch is listed alongside wsId rather than instead of it. wsId is what
    // the effect READS (the storage keys are namespaced by it); the epoch is
    // what asserts the tenant changed. They move together on a switch, and
    // listing both means neither a repeated id nor a repeated epoch can leave a
    // resume running for a workspace the user has left.
    // `flow` is listed because the storage keys are namespaced by it: the
    // first-run and add-brand runs must resume from their OWN draft, never each
    // other's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId, wsScoped, workspaceEpoch, flow]);

  // Reset every tenant-derived answer when the tenant changes.
  //
  // The resume effect above restores whatever the NEW workspace has in storage,
  // but it cannot un-say what the old one put on screen: switching mid-onboarding
  // otherwise leaves workspace A's generated brand, its category and its scope
  // sitting in the form, one Confirm away from being written into workspace B.
  // That is the cross-tenant write, and this effect is what closes it.
  //
  // Ordered before the resume effect's restore by the dep array they share, so
  // a workspace that DOES have a draft still gets it back.
  // Guard by COMPARING THE EPOCH, not by "skip the first run".
  //
  // The previous `firstEpochRef = true; if (first) { first = false; return; }`
  // shape is not idempotent, and under StrictMode (dev) effects mount twice:
  // the first invoke burned the guard, the second ran the reset for real. So on
  // every dev page load this effect wiped state nothing had changed —
  // including a draft the resume effect above had just restored, which made the
  // finished-but-unconfirmed resume path silently untestable locally. Found by
  // driving it; the add-brand draft never came back.
  //
  // Comparing values is naturally idempotent: a second invoke at the same epoch
  // sees no change and returns. Same behaviour in production, correct behaviour
  // in dev, and no reliance on how many times React chooses to run an effect.
  const lastResetEpochRef = React.useRef(workspaceEpoch);
  useEffect(() => {
    if (lastResetEpochRef.current === workspaceEpoch) return;
    lastResetEpochRef.current = workspaceEpoch;
    genPollRef.current.stop = true;
    genBusyRef.current = false;
    revealRef.current = { profile: false, icps: false, keywords: false };
    setGen(null);
    setDraft(null);
    setGenerating(false);
    setConfirmedBrandId(null);
    setFirstScanRunning(false);
    setNotified(false);
    setRevealHeadline("");
    setLiveMsg("");
    setPaywall(null);
    setDump("");
    setUrl("");
    setBrand({ name: "", website_url: "", description: "" });
    // In add-brand mode the transcript is the BRAND's answers, not the
    // workspace's, so a tenant change must blank them rather than re-seed from
    // a workspace that never held them.
    setNewCategoryId(null);
    setNewScope(null);
    setNewCity("");
    setCategoryLabel(newMode ? "" : (workspace?.business_category_label || ""));
    setScopeLabel("");
    setScopeCity(newMode ? "" : (workspace?.market_city || ""));
    setDumpMode(false);
    setStep(
      newMode ? "category"
        : !workspace?.business_category ? "category"
        : !workspace?.market_scope ? "scope"
        : "input"
    );
    // `workspace` is read only to re-derive the opening step for the tenant we
    // just landed in; it is not what triggers the reset. Listing it would re-run
    // this on every unrelated workspace refresh and wipe the user's typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceEpoch]);

  const MIN_DUMP = 150, MAX_DUMP = 8000;
  const submitInput = (e) => {
    e?.preventDefault?.();
    if (dumpMode) {
      if (dump.trim().length < MIN_DUMP) {
        toast.error(`Add a bit more — at least ${MIN_DUMP} characters.`);
        return;
      }
      runGenerate();
      return;
    }
    if (!url.trim()) {
      toast.error("Please paste your website URL.");
      return;
    }
    if (!/^https?:\/\//i.test(url.trim())) {
      setUrl((u) => (u.startsWith("http") ? u : "https://" + u));
    }
    // Brand analysis is free — no cost confirmation, just start analysing.
    runGenerate();
  };

  const confirmBrand = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = {
        brand_id: "",
        brand: {
          brand_id: "",
          workspace_id: workspace?.workspace_id || "",
          name: brand.name || draft.profile?.name || "My Brand",
          website_url: dumpMode ? "" : (brand.website_url || url),
          content_dump: dumpMode ? dump.trim().slice(0, MAX_DUMP) : null,
          description: brand.description || "",
          status: "confirmed",
        },
        profile: {
          brand_id: "",
          brand_summary: draft.profile?.brand_summary || "",
          category: draft.profile?.category || "",
          value_props: draft.profile?.value_props || [],
          differentiators: draft.profile?.differentiators || [],
          target_geography: draft.profile?.target_geography || "",
          price_point: draft.profile?.price_point || "",
          fit_score: draft.profile?.fit_score || 0,
          status: "confirmed",
        },
        icps: (draft.icps || []).map((icp) => ({
          icp_id: "",
          brand_id: "",
          name: icp.name || "",
          description: icp.description || "",
          role_titles: icp.role_titles || [],
          pain_points: icp.pain_points || [],
          buying_triggers: icp.buying_triggers || [],
          industries: icp.industries || [],
          company_sizes: icp.company_sizes || [],
        })),
        keywords: (draft.keywords || []).map((k) => ({
          keyword_id: "",
          brand_id: "",
          text: k.text || "",
          stage: k.stage || "ready",
          default_selected: k.default_selected !== false,
        })),
        competitors: [],
      };
      const resp = await api.post("/brands/confirm", payload);
      const newBrandId = resp.data.brand_id;
      // ── SEAT THE BRAND WE JUST MADE ────────────────────────────────────────
      //
      // `refreshBrand` alone does NOT do this, and the reason is easy to miss:
      // its resolution order starts with "the brand we are already seated on,
      // if it still exists". In add-brand mode that is brand #1, which still
      // exists — so the refresh keeps it and the brand the user just spent two
      // minutes creating is silently not the one they land on. They would then
      // run their first scan against the wrong client.
      //
      // So say it explicitly. `switchBrand` clears the seat, bumps the epoch
      // and re-derives, which is exactly the transition every brand-scoped
      // screen needs — and it records the choice as this workspace's stored
      // preference, because creating a brand is choosing it.
      //
      // First-run onboarding goes through `refreshBrand` as before: there is
      // nothing seated to prefer, so derivation lands on the new brand anyway,
      // and a `switchBrand` there would be a redundant extra round trip.
      if (newMode && newBrandId) {
        try {
          await switchBrand(newBrandId);
        } catch (_) {
          // The brand IS saved; only the seating failed. Fall back to
          // derivation rather than stranding the user on a half-done screen.
          await refreshBrand();
        }
        await refreshWorkspace();
      } else {
        await Promise.all([refreshBrand(), refreshWorkspace()]);
      }
      sessionStorage.removeItem("lm_skip_onboarding");
      if (jobKey) sessionStorage.removeItem(jobKey);
      if (draftKey) sessionStorage.removeItem(draftKey);
      // Instead of jumping to the dashboard, run the FIRST SCAN as the last
      // chat step (required) — pick keywords + platforms, then launch.
      setConfirmedBrandId(newBrandId);
      setStep("firstscan");
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not save your brand."));
    } finally {
      setSaving(false);
    }
  };

  const runFirstScan = async (platforms) => {
    if (!confirmedBrandId) return;
    setFirstScanRunning(true);
    try {
      // Async scan — returns a scan_id instantly. The Find Customers page will
      // detect the running scan and show a persistent loader (survives refresh).
      await api.post("/scans", {
        brand_id: confirmedBrandId,
        feature: "find_customers",
        platforms,
      });
      navigate("/app/find-customers", {
        replace: true,
        state: { justOnboarded: true, brandId: confirmedBrandId },
      });
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not start your scan."));
      setFirstScanRunning(false);
    }
  };

  const first = (user?.name || "").split(" ")[0];

  // What's been answered already → reply bubbles shown above the active step.
  const done = {
    category: ["scope", "input", "generating", "review", "firstscan"].includes(step),
    scope: ["input", "generating", "review", "firstscan"].includes(step),
    website: ["generating", "review", "firstscan"].includes(step),
    review: step === "firstscan",
  };

  return (
    <ChatLayout
      scrollRef={scrollRef}
      onSkip={() => {
        sessionStorage.setItem("lm_skip_onboarding", "1");
        navigate("/app/dashboard", { replace: true });
      }}
      onSignout={async () => { await logout(); navigate("/", { replace: true }); }}
    >
      {/* Intro */}
      <Assistant>
        {/* COPY SLOT onboarding.intro.newbrand — ORI, PLEASE REVIEW. The job of
            this variant is to make clear that the answers coming up are about
            the NEW brand and will not disturb the existing one — that is the
            question a careful user will actually have. */}
        {newMode ? (
          <p>
            {first ? `Hey ${first}! ` : "Hey there! "}
            Let's add another brand. I'll ask the same few questions again — each brand gets its own
            answers, so nothing here changes the one you already set up. ✨
          </p>
        ) : (
          <p>
            {first ? `Hey ${first}! ` : "Hey there! "}
            I'll set up your Leadmato in a couple of minutes — a few quick questions and I'll read your
            website for you. ✨
          </p>
        )}
      </Assistant>

      {/* 1 · Category */}
      <Assistant>
        <p className="font-medium">
          {newMode ? "What kind of business is this one?" : "First — what kind of business are you?"}
        </p>
        <p className="text-lm-text3 text-sm mt-1">
          This tells me which platforms and registers to tune so I find the right customers for you.
        </p>
      </Assistant>
      {done.category ? (
        <UserReply onEdit={() => setStep("category")}>{categoryLabel || "My business"}</UserReply>
      ) : step === "category" ? (
        <ChatCategoryStep
          localOnly={newMode}
          onAccepted={async (label, categoryId) => {
            setCategoryLabel(label);
            if (newMode) {
              // Held for POST /brands/generate. Nothing was written server-side,
              // so there is no workspace change to refresh for.
              setNewCategoryId(categoryId);
            } else {
              await refreshWorkspace?.();
            }
            setStep("scope");
          }}
        />
      ) : null}

      {/* 2 · Scope */}
      {done.category && (
        <>
          <Assistant>
            <p className="font-medium">
              {newMode ? "Where are this brand's customers?" : "Where are your customers?"}
            </p>
            {/* Honest about what a country choice actually buys. `style_query`
                only attaches a country/region param for platforms whose vendor
                supports one — today web, forums and tiktok. Reddit, X, LinkedIn
                and Facebook are searched identically whichever country you pick.
                Picking a city is genuinely different (it passes a location
                param), which is why the two are now described differently
                instead of both being sold as "the biggest lever". */}
            <p className="text-lm-text3 text-sm mt-1">
              Picking your city is the strongest signal I have. For a country, I'll narrow the
              search wherever the platform lets me — some allow it, some don't — so it helps, but
              it won't catch everything.
            </p>
          </Assistant>
          {done.scope ? (
            <UserReply onEdit={() => setStep("scope")}>
              {/* In add-brand mode the workspace's own market is NOT a fallback
                  for this brand's — showing it would state, in the transcript,
                  an answer the user never gave for this brand. */}
              {scopeLabel || (newMode ? "" : formatScope(workspace?.market_scope))}
              {(() => {
                const c = scopeCity || (newMode ? "" : workspace?.market_city);
                return c ? ` · ${c}` : "";
              })()}
            </UserReply>
          ) : step === "scope" ? (
            <ChatScopeStep
              localOnly={newMode}
              // A fresh brand starts from nothing. Pre-filling with the
              // workspace's market is precisely the inheritance `?new=1` exists
              // to prevent — a pre-ticked answer is an answer most people keep.
              initialScope={newMode ? "" : workspace?.market_scope}
              initialCity={newMode ? "" : workspace?.market_city}
              categoryLabel={categoryLabel || (newMode ? "" : workspace?.business_category_label)}
              onSaved={async (label, city, scopeId) => {
                setScopeLabel(label);
                setScopeCity(city || "");
                if (newMode) {
                  setNewScope(scopeId);
                  setNewCity(city || "");
                } else {
                  await refreshWorkspace?.();
                }
                setStep("input");
              }}
            />
          ) : null}
        </>
      )}

      {/* 3 · Website */}
      {done.scope && (
        <>
          <Assistant>
            <p className="font-medium">
              {newMode
                ? "Last thing — drop this brand's website link."
                : "Last thing — drop your website link."}
            </p>
            <p className="text-lm-text3 text-sm mt-1">
              I'll read it and draft your brand, ideal customers, and the buyer keywords I'll listen for.
              It's <span className="font-medium text-lm-primary">free</span> — I'll start as soon as you hit go.
            </p>
          </Assistant>
          {done.website ? (
            <UserReply mono onEdit={() => setStep("input")}>
              {dumpMode ? "📋 Pasted brand content" : url}
            </UserReply>
          ) : step === "input" ? (
            <>
              {cooldown && (
                <div
                  role="status"
                  className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                >
                  You just ran a brand analysis — you can create another in about{" "}
                  <span className="font-semibold">
                    {cooldown.mins} minute{cooldown.mins !== 1 ? "s" : ""}
                  </span>{" "}
                  (around {cooldown.at}). It's free, just one every 30 minutes.
                </div>
              )}
              <ChatUrlComposer
                url={url}
                setUrl={setUrl}
                dumpMode={dumpMode}
                setDumpMode={setDumpMode}
                dump={dump}
                setDump={setDump}
                minDump={MIN_DUMP}
                maxDump={MAX_DUMP}
                onSubmit={submitInput}
              />
            </>
          ) : null}
        </>
      )}

      {/* 3.5 · The interview — three questions asked WHILE the site is read.
             Stays in the transcript after it resolves (it is chat history, and
             the review below is the next turn of the same conversation), which
             is also why it is not gated on step === "generating". */}
      {gen?.interview && gen.status !== "error" &&
        ["generating", "review", "firstscan"].includes(step) && (
        <InterviewStep
          gen={gen}
          onSubmit={submitInterview}
          // Progress lives inside the interview block only while that block IS
          // the wait. Once we move to review, GenRibbon owns it — two bars for
          // one job would be two answers to "how far along is this".
          showProgress={step === "generating" && gen.interview.state !== "resolved"}
        />
      )}

      {/* 4 · Generating (pre-profile full loader; collapses to a ribbon once
             the brand profile lands and we switch to the review step) */}
      {step === "generating" && (gen?.interview?.state !== "pending" || gen?.status === "error") && (
        <Assistant>
          <BrandGenLoader gen={gen} onRetry={retryGenerate} notified={notified} onNotify={handleNotify} />
        </Assistant>
      )}

      {/* 5 · Review — fills progressively as ICPs then keywords stream in */}
      {step === "review" && draft && (
        <>
          <Assistant>
            <p className="font-medium">
              {revealHeadline || "Here's what I found 👇"}
            </p>
            <p className="text-lm-text3 text-sm mt-1">
              Tweak anything that feels off — small fixes here make every scan sharper. Then hit confirm.
            </p>
          </Assistant>
          <ChatReview
            brand={brand} setBrand={setBrand}
            draft={draft} setDraft={setDraft}
            saving={saving} onConfirm={confirmBrand}
            gen={gen} genDone={genDone} liveMsg={liveMsg} onRetry={retryGenerate}
            notified={notified} onNotify={handleNotify}
          />
        </>
      )}

      {/* 6 · First scan */}
      {step === "firstscan" && (
        <>
          <Assistant>
            <p className="font-medium">Brand saved ✓ Now let's run your first scan.</p>
            <p className="text-lm-text3 text-sm mt-1">
              Pick the keywords I'll listen for and the platforms to search (up to {MAX_SCAN_PLATFORMS}).
              Then hit go — I'll start hunting while you head to your leads board.
            </p>
          </Assistant>
          <FirstScanStep
            brandId={confirmedBrandId}
            running={firstScanRunning}
            onRun={runFirstScan}
          />
        </>
      )}

      <PaywallModal open={!!paywall} info={paywall} onClose={() => setPaywall(null)} />
    </ChatLayout>
  );
}
